import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Camera, Package, CheckCircle, XCircle, AlertTriangle, ArrowLeft, Plus, Thermometer, FileText, QrCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

import { API } from '@/config';

export default function MobileReceiptPage() {
  const { receiptId } = useParams();
  const navigate = useNavigate();
  
  const [receipt, setReceipt] = useState(null);
  const [po, setPo] = useState(null);
  const [truck, setTruck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentItem, setCurrentItem] = useState(null);
  const [showItemForm, setShowItemForm] = useState(false);
  
  // Item form state
  const [itemForm, setItemForm] = useState({
    item_id: '',
    item_name: '',
    quantity_received: '',
    uom: '',
    lot_number: '',
    production_date: '',
    expiration_date: '',
    temperature_reading: '',
    qa_result: 'accept',
    qa_notes: '',
    hold_reason: '',
    photos: []
  });

  useEffect(() => {
    fetchData();
  }, [receiptId]);

  const fetchData = async () => {
    try {
      const receiptRes = await axios.get(`${API}/warehouse/receipts/${receiptId}`);
      setReceipt(receiptRes.data);
      
      // Fetch truck details
      if (receiptRes.data.truck_id) {
        const truckRes = await axios.get(`${API}/warehouse/trucks?truck_id=${receiptRes.data.truck_id}`);
        if (truckRes.data.length > 0) {
          setTruck(truckRes.data[0]);
        }
      }
      
      // Fetch PO if linked
      if (receiptRes.data.po_number) {
        try {
          const poRes = await axios.get(`${API}/warehouse/purchase-orders/number/${receiptRes.data.po_number}`);
          setPo(poRes.data);
        } catch (error) {
          console.log('PO not found');
        }
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load receipt');
      setLoading(false);
    }
  };

  const handleStartItem = (poLineItem = null) => {
    if (poLineItem) {
      setItemForm({
        ...itemForm,
        item_id: poLineItem.item_id,
        item_name: poLineItem.item_name,
        uom: poLineItem.unit,
        quantity_received: poLineItem.quantity_remaining.toString()
      });
    }
    setShowItemForm(true);
  };

  const handlePhotoCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setItemForm({
          ...itemForm,
          photos: [...itemForm.photos, reader.result]
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveItem = async () => {
    try {
      // Generate license plate
      const lpData = {
        item_id: itemForm.item_id,
        item_name: itemForm.item_name,
        quantity: parseFloat(itemForm.quantity_received),
        uom: itemForm.uom,
        lot_number: itemForm.lot_number,
        expiration_date: itemForm.expiration_date || null,
        temperature_reading: itemForm.temperature_reading ? parseFloat(itemForm.temperature_reading) : null,
        photos: itemForm.photos
      };
      
      const lpRes = await axios.post(`${API}/warehouse/receipts/${receiptId}/generate-lp`, lpData);
      
      // Create receipt line item
      const lineItem = {
        item_id: itemForm.item_id,
        item_name: itemForm.item_name,
        lp_number: lpRes.data.lp_number,
        quantity_received: parseFloat(itemForm.quantity_received),
        uom: itemForm.uom,
        lot_number: itemForm.lot_number,
        production_date: itemForm.production_date || null,
        expiration_date: itemForm.expiration_date || null,
        temperature_reading: itemForm.temperature_reading ? parseFloat(itemForm.temperature_reading) : null,
        qa_result: itemForm.qa_result,
        qa_notes: itemForm.qa_notes || null,
        hold_reason: itemForm.qa_result === 'hold' ? itemForm.hold_reason : null,
        photos: itemForm.photos
      };
      
      // Update receipt with new line item
      const updatedReceipt = {
        ...receipt,
        line_items: [...receipt.line_items, lineItem],
        updated_at: new Date().toISOString()
      };
      
      await axios.put(`${API}/warehouse/receipts/${receiptId}`, updatedReceipt);
      
      // If item is on hold, create hold record
      if (itemForm.qa_result === 'hold') {
        await axios.post(`${API}/warehouse/holds`, {
          lp_number: lpRes.data.lp_number,
          item_id: itemForm.item_id,
          item_name: itemForm.item_name,
          lot_number: itemForm.lot_number,
          quantity_held: parseFloat(itemForm.quantity_received),
          reason: itemForm.hold_reason,
          held_by: receipt.completed_by,
          location_id: 'HOLD-AREA-01',
          photos: itemForm.photos
        });
      }
      
      toast.success(`Item received! LP: ${lpRes.data.lp_number}`);
      
      // Reset form
      setItemForm({
        item_id: '',
        item_name: '',
        quantity_received: '',
        uom: '',
        lot_number: '',
        production_date: '',
        expiration_date: '',
        temperature_reading: '',
        qa_result: 'accept',
        qa_notes: '',
        hold_reason: '',
        photos: []
      });
      setShowItemForm(false);
      fetchData();
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error('Failed to save item');
    }
  };

  const handleCompleteReceipt = async () => {
    try {
      const updatedReceipt = {
        ...receipt,
        status: 'completed',
        updated_at: new Date().toISOString()
      };
      
      await axios.put(`${API}/warehouse/receipts/${receiptId}`, updatedReceipt);
      
      // Update PO quantities if linked
      if (po) {
        const lineUpdates = receipt.line_items.map(item => ({
          line_number: po.line_items.find(li => li.item_id === item.item_id)?.line_number || 1,
          quantity_received: item.quantity_received
        }));
        
        await axios.put(`${API}/warehouse/purchase-orders/${po.po_id}/receive`, {
          line_items: lineUpdates
        });
      }
      
      toast.success('Receipt completed!');
      navigate('/warehouse/receipts');
    } catch (error) {
      console.error('Error completing receipt:', error);
      toast.error('Failed to complete receipt');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-blue-600 text-white p-4 sticky top-0 z-10 shadow-md">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/warehouse/receipts')} className="p-2 hover:bg-blue-700 rounded">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Mobile Receiving</h1>
            <p className="text-sm text-blue-100">
              {po ? `PO: ${po.po_number}` : truck?.confirmation_number || 'Receipt Processing'}
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4 pb-24">
        {/* Delivery Info Card */}
        <Card className="p-4">
          <h2 className="font-bold text-lg mb-3 flex items-center gap-2">
            <Package className="w-5 h-5" />
            Delivery Information
          </h2>
          <div className="space-y-2 text-sm">
            {truck && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600">Carrier:</span>
                  <span className="font-medium">{truck.carrier}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Driver:</span>
                  <span className="font-medium">{truck.driver_name || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">License Plate:</span>
                  <span className="font-medium">{truck.license_plate || 'N/A'}</span>
                </div>
              </>
            )}
            {po && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600">Vendor:</span>
                  <span className="font-medium">{po.vendor}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Expected Date:</span>
                  <span className="font-medium">{po.expected_delivery_date}</span>
                </div>
              </>
            )}
          </div>
        </Card>

        {/* PO Line Items */}
        {po && (
          <Card className="p-4">
            <h2 className="font-bold text-lg mb-3">Expected Items</h2>
            <div className="space-y-3">
              {po.line_items.map((item) => {
                const received = receipt.line_items.find(li => li.item_id === item.item_id);
                const isFullyReceived = item.quantity_remaining === 0;
                
                return (
                  <div key={item.line_number} className="border rounded-lg p-3 bg-white">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{item.item_name}</p>
                        <p className="text-xs text-gray-500">SKU: {item.sku}</p>
                      </div>
                      {isFullyReceived && (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      )}
                    </div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Ordered:</span>
                      <span className="font-medium">{item.quantity_ordered} {item.unit}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Remaining:</span>
                      <span className={`font-medium ${item.quantity_remaining === 0 ? 'text-green-600' : 'text-blue-600'}`}>
                        {item.quantity_remaining} {item.unit}
                      </span>
                    </div>
                    {!isFullyReceived && (
                      <Button
                        onClick={() => handleStartItem(item)}
                        className="w-full mt-2"
                        size="sm"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Receive Item
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Received Items */}
        {receipt.line_items.length > 0 && (
          <Card className="p-4">
            <h2 className="font-bold text-lg mb-3">Received Items ({receipt.line_items.length})</h2>
            <div className="space-y-3">
              {receipt.line_items.map((item, idx) => (
                <div key={idx} className="border rounded-lg p-3 bg-white">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <p className="font-medium text-sm">{item.item_name}</p>
                      <p className="text-xs text-gray-500">LP: {item.lp_number}</p>
                      <p className="text-xs text-gray-500">Lot: {item.lot_number}</p>
                    </div>
                    <div className="text-right">
                      {item.qa_result === 'accept' && <CheckCircle className="w-5 h-5 text-green-600" />}
                      {item.qa_result === 'hold' && <AlertTriangle className="w-5 h-5 text-yellow-600" />}
                      {item.qa_result === 'reject' && <XCircle className="w-5 h-5 text-red-600" />}
                    </div>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-600">Qty:</span>
                    <span className="font-medium ml-2">{item.quantity_received} {item.uom}</span>
                  </div>
                  {item.temperature_reading && (
                    <div className="text-sm mt-1">
                      <span className="text-gray-600">Temp:</span>
                      <span className="font-medium ml-2">{item.temperature_reading}°F</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Add Item Button (if no PO or for additional items) */}
        {!showItemForm && (
          <Button
            onClick={() => handleStartItem()}
            className="w-full"
            variant="outline"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Item (No PO)
          </Button>
        )}
      </div>

      {/* Item Form Modal */}
      {showItemForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-20 overflow-y-auto">
          <div className="min-h-screen p-4">
            <Card className="max-w-lg mx-auto my-8 p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-lg">Receive Item</h2>
                <button onClick={() => setShowItemForm(false)} className="text-gray-500">✕</button>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Item Name *</Label>
                  <Input
                    value={itemForm.item_name}
                    onChange={(e) => setItemForm({...itemForm, item_name: e.target.value})}
                    placeholder="Enter item name"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Quantity *</Label>
                    <Input
                      type="number"
                      value={itemForm.quantity_received}
                      onChange={(e) => setItemForm({...itemForm, quantity_received: e.target.value})}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label>Unit *</Label>
                    <Input
                      value={itemForm.uom}
                      onChange={(e) => setItemForm({...itemForm, uom: e.target.value})}
                      placeholder="lbs, each"
                    />
                  </div>
                </div>

                <div>
                  <Label>Lot Number *</Label>
                  <Input
                    value={itemForm.lot_number}
                    onChange={(e) => setItemForm({...itemForm, lot_number: e.target.value})}
                    placeholder="Enter lot number"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Production Date</Label>
                    <Input
                      type="date"
                      value={itemForm.production_date}
                      onChange={(e) => setItemForm({...itemForm, production_date: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Expiration Date</Label>
                    <Input
                      type="date"
                      value={itemForm.expiration_date}
                      onChange={(e) => setItemForm({...itemForm, expiration_date: e.target.value})}
                    />
                  </div>
                </div>

                <div>
                  <Label>Temperature (°F)</Label>
                  <div className="flex gap-2">
                    <Thermometer className="w-5 h-5 text-gray-400" />
                    <Input
                      type="number"
                      step="0.1"
                      value={itemForm.temperature_reading}
                      onChange={(e) => setItemForm({...itemForm, temperature_reading: e.target.value})}
                      placeholder="34.0"
                    />
                  </div>
                </div>

                <div>
                  <Label>QA Result *</Label>
                  <Select value={itemForm.qa_result} onValueChange={(value) => setItemForm({...itemForm, qa_result: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="accept">Accept</SelectItem>
                      <SelectItem value="hold">Hold</SelectItem>
                      <SelectItem value="reject">Reject</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {itemForm.qa_result === 'hold' && (
                  <div>
                    <Label>Hold Reason *</Label>
                    <Textarea
                      value={itemForm.hold_reason}
                      onChange={(e) => setItemForm({...itemForm, hold_reason: e.target.value})}
                      placeholder="Reason for hold..."
                      rows={2}
                    />
                  </div>
                )}

                {(itemForm.qa_result === 'hold' || itemForm.qa_result === 'reject') && (
                  <div>
                    <Label>QA Notes</Label>
                    <Textarea
                      value={itemForm.qa_notes}
                      onChange={(e) => setItemForm({...itemForm, qa_notes: e.target.value})}
                      placeholder="Additional notes..."
                      rows={2}
                    />
                  </div>
                )}

                <div>
                  <Label>Photos</Label>
                  <div className="mt-2">
                    <label className="flex items-center justify-center w-full p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                      <Camera className="w-6 h-6 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">Take Photo</span>
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={handlePhotoCapture}
                      />
                    </label>
                  </div>
                  {itemForm.photos.length > 0 && (
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      {itemForm.photos.map((photo, idx) => (
                        <img key={idx} src={photo} alt={`Photo ${idx + 1}`} className="w-full h-20 object-cover rounded" />
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-2 pt-4">
                  <Button variant="outline" onClick={() => setShowItemForm(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveItem}
                    className="flex-1"
                    disabled={!itemForm.item_name || !itemForm.quantity_received || !itemForm.uom || !itemForm.lot_number}
                  >
                    Save Item
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 shadow-lg">
        <div className="max-w-lg mx-auto">
          <Button
            onClick={handleCompleteReceipt}
            className="w-full bg-green-600 hover:bg-green-700"
            disabled={receipt.line_items.length === 0}
          >
            <CheckCircle className="w-5 h-5 mr-2" />
            Complete Receipt ({receipt.line_items.length} items)
          </Button>
        </div>
      </div>
    </div>
  );
}
